import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Zap, ArrowUpDown, Sprout, Sparkles, Clock } from "lucide-react";
import api, { TIER_META } from "../lib/api";

export default function Queue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tier, setTier] = useState("");
  const [sort, setSort] = useState("recency");
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [themeOnly, setThemeOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/submissions/queue", { params: { tier: tier || undefined, sort, theme: (themeOnly && cfg?.active_theme?.id) || undefined } });
      setItems(res.data);
    } catch (e) {
      toast.error("Failed to load queue");
    } finally { setLoading(false); }
  }, [tier, sort, themeOnly, cfg]);

  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const claim = async (id) => {
    try {
      await api.post(`/submissions/${id}/claim`);
      navigate(`/app/compose/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim");
      load();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Open Queue</h1>
          <p className="text-zinc-400 mt-2">Claim a submission, write a killer roast, get paid the flat rate.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select data-testid="filter-tier" value={tier} onChange={(e) => setTier(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2 text-sm focus:border-brand focus:outline-none">
          <option value="">All tiers</option>
          {Object.keys(TIER_META).map((k) => <option key={k} value={k}>{TIER_META[k].label}</option>)}
        </select>
        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2 text-sm focus:border-brand focus:outline-none">
          <option value="recency">Newest first</option>
          <option value="price_desc">Highest pay</option>
          <option value="price_asc">Lowest pay</option>
        </select>
        <button data-testid="refresh-queue" onClick={load} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm transition-colors">
          <ArrowUpDown size={14} /> Refresh
        </button>
      </div>

      {cfg?.bonus_window && (
        <div data-testid="bonus-banner" className="rounded-sm border border-brand-accent/40 bg-brand-accent/10 p-4 mb-4 flex items-center gap-3">
          <Zap className="text-brand-accent shrink-0" size={20} />
          <div className="flex-1">
            <div className="text-sm font-bold text-zinc-100">{cfg.bonus_window.label || "Bonus roaster window"} — {cfg.bonus_window.multiplier}× payout</div>
            <div className="text-xs text-zinc-400">Roasts you submit now lock in the boosted rate.{cfg.bonus_window.ends_at ? ` Ends ${new Date(cfg.bonus_window.ends_at).toLocaleString()}.` : ""}</div>
          </div>
        </div>
      )}

      {cfg?.active_theme && (
        <div data-testid="queue-theme-banner" className="rounded-sm border border-brand/30 bg-brand/10 p-4 mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0"><Sparkles className="text-brand shrink-0" size={18} /><div className="min-w-0"><div className="text-sm font-bold text-zinc-100">{cfg.active_theme.label}</div>{cfg.active_theme.description && <div className="text-xs text-zinc-400 truncate">{cfg.active_theme.description}</div>}</div></div>
          <button data-testid="theme-filter-toggle" onClick={() => setThemeOnly((v) => !v)}
            className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${themeOnly ? "bg-brand text-black border-brand" : "border-brand/40 text-brand hover:bg-brand/10"}`}>
            {themeOnly ? "Showing theme only" : "Filter to theme"}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 py-16 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-sm border border-white/10 bg-zinc-900/40 p-12 text-center">
          <Flame className="mx-auto text-zinc-700 mb-3" size={36} />
          <p className="text-zinc-400">The queue is empty right now. Check back soon.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((s, i) => {
            const meta = TIER_META[s.tier];
            return (
              <div key={s.id} data-testid={`queue-item-${s.id}`} className="animate-fade-up rounded-sm border border-white/10 bg-zinc-900/60 p-5 flex flex-col hover:-translate-y-0.5 transition-transform"
                style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ color: meta.color, background: `${meta.color}1a` }}>{meta.label}</span>
                  <div className="flex items-center gap-2">
                    {s.is_seed && <span data-testid={`seed-badge-${s.id}`} title="Posted by the house to warm up the queue" className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-zinc-300"><Sprout size={11} /> House pick</span>}
                    {s.boosted && <span className="flex items-center gap-1 text-[11px] text-brand-accent font-bold"><Zap size={12} /> Boosted</span>}
                  </div>
                </div>
                {s.content_type === "image" ? (
                  <img src={s.content} alt="" className="rounded-sm border border-white/10 max-h-40 object-cover mb-3" />
                ) : (
                  <p className="text-sm text-zinc-200 leading-relaxed mb-3 line-clamp-4">"{s.content}"</p>
                )}
                <div className="mt-auto flex items-center justify-between pt-3 border-t border-white/10">
                  <div>
                    <div className="text-xs text-zinc-500 uppercase tracking-widest">You earn</div>
                    {s.bonus_payout_amount ? (
                      <div className="font-heading text-xl font-bold text-brand-accent">{s.symbol}{s.bonus_payout_amount.toFixed(2)} <span className="text-xs text-zinc-500 line-through">{s.symbol}{s.payout_amount.toFixed(2)}</span></div>
                    ) : (
                      <div className="font-heading text-xl font-bold text-tier-mild">{s.symbol}{s.payout_amount.toFixed(2)}</div>
                    )}
                  </div>
                  <button data-testid={`claim-btn-${s.id}`} onClick={() => claim(s.id)}
                    className="px-5 py-2 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm transition-colors">
                    Claim & Roast
                  </button>
                </div>
                <div className="text-[11px] text-zinc-600 mt-2">{s.roast_count}/5 roasts submitted</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
